<template>
  <v-container class="lx-page">
    <header class="lx-page-head">
      <span class="kicker">Research · Trading Strategy</span>
      <h1>Trading Strategy</h1>
      <h4>
        A simple directional rule turns each weekly forecast into a position:
        long when Janus is above the market consensus, short when below, held
        to the quarter horizon. The chart compares the cumulative return of
        that rule, per unit of capital deployed, with a passive buy-and-hold of
        the same market.
      </h4>
    </header>

    <div class="lx-toolbar">
      <label class="lx-field lx-field--wide">
        <span class="lx-field__label">Market</span>
        <select class="form-control minimal" v-model="value">
          <option v-for="m in markets" :value="m" :key="m">{{ m }}</option>
        </select>
      </label>
    </div>

    <section class="lx-panel">
      <div class="lx-panel__head">
        <span class="lx-panel__title">{{ value || "Select a market" }} · Janus strategy vs buy &amp; hold</span>
        <span class="lx-panel__meta" v-if="rangeLabel">{{ rangeLabel }}</span>
      </div>
      <div class="lx-kpi-strip">
        <div class="lx-kpi">
          <span class="lx-kpi__label">Edge vs buy &amp; hold</span>
          <span class="lx-kpi__value lx-kpi__value--sm" :class="edgeClass">{{ edgeText }}</span>
          <span class="lx-kpi__sub">annualised</span>
        </div>
        <div class="lx-kpi">
          <span class="lx-kpi__label">Directional accuracy</span>
          <span class="lx-kpi__value lx-kpi__value--sm">{{ pctText(stats.dir_acc) }}</span>
          <span class="lx-kpi__sub">sign of the move called right</span>
        </div>
        <div class="lx-kpi">
          <span class="lx-kpi__label">Beat consensus</span>
          <span class="lx-kpi__value lx-kpi__value--sm">{{ pctText(stats.beat) }}</span>
          <span class="lx-kpi__sub">Janus closer than consensus</span>
        </div>
        <div class="lx-kpi">
          <span class="lx-kpi__label">Final value</span>
          <span class="lx-kpi__value lx-kpi__value--sm">{{ finalText }}</span>
          <span class="lx-kpi__sub">Janus / buy &amp; hold, start = 100</span>
        </div>
      </div>
      <div class="lx-chart__frame">
        <div class="lx-legend mb-3" aria-label="Chart legend">
          <span class="lx-legend__item"><span class="lx-legend__swatch lx-legend__swatch--blue"></span>Janus strategy</span>
          <span class="lx-legend__item"><span class="lx-legend__swatch lx-legend__swatch--hist"></span>Buy &amp; hold</span>
        </div>
        <div
          ref="container"
          class="lx-chart"
          style="height: 440px"
          role="img"
          :aria-label="`${value} Janus strategy vs buy and hold`"
        ></div>
      </div>
    </section>

    <p class="lx-footnote">
      Both curves indexed to 100 at the first forecast, per unit of capital
      deployed continuously. Points are the forecast vintages in the archive, so
      spacing between them is uneven.
    </p>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  createTimeChart,
  setTimeDomain,
  setValueDomain,
  addLine,
  toPoints,
  fmtDate,
  css,
  disposeChart,
} from "@/plugins/lxChart";

export default {
  name: "trading-strategy",
  data() {
    return {
      value: "",
      chart: null,
    };
  },
  computed: {
    ...mapGetters(["getTradingStrategy"]),
    markets() {
      return this.getTradingStrategy ? Object.keys(this.getTradingStrategy) : [];
    },
    current() {
      const d = this.getTradingStrategy;
      return d && this.value ? d[this.value] : null;
    },
    stats() {
      const c = this.current;
      return c ? { edge_yr: c.edge_yr, dir_acc: c.dir_acc, beat: c.beat } : {};
    },
    curves() {
      const c = this.current;
      if (!c || !c.dates) return { strat: [], bh: [] };
      return {
        strat: toPoints(c.dates.map((d, i) => [d, c.strat[i]])),
        bh: toPoints(c.dates.map((d, i) => [d, c.bh[i]])),
      };
    },
    rangeLabel() {
      const p = this.curves.strat;
      return p.length ? `${fmtDate(p[0][0], "MMM yyyy")} – ${fmtDate(p[p.length - 1][0], "MMM yyyy")}` : "";
    },
    edgeText() {
      const e = parseFloat(this.stats.edge_yr);
      return Number.isFinite(e) ? `${e >= 0 ? "+" : ""}${e.toFixed(1)}% / yr` : "–";
    },
    edgeClass() {
      const e = parseFloat(this.stats.edge_yr);
      return !Number.isFinite(e) ? "" : e >= 0 ? "lx-pos" : "lx-neg";
    },
    finalText() {
      const s = this.curves.strat;
      const b = this.curves.bh;
      if (!s.length || !b.length) return "–";
      return `${s[s.length - 1][1].toFixed(0)} / ${b[b.length - 1][1].toFixed(0)}`;
    },
  },
  methods: {
    ...mapActions(["fetchTradingStrategy"]),
    pctText(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? `${n.toFixed(n % 1 ? 1 : 0)}%` : "–";
    },
    draw() {
      if (!this.$refs.container) return;
      if (!this.chart) {
        this.chart = createTimeChart(this.$refs.container);
        this.chart.draw();
      }
      const chart = this.chart;
      chart.removeAllSeries();
      const { strat, bh } = this.curves;
      if (!strat.length) return;
      const all = [...strat, ...bh];
      setTimeDomain(
        chart,
        Math.min(...all.map((p) => p[0])),
        Math.max(...all.map((p) => p[0]))
      );
      setValueDomain(chart, [100, ...all.map((p) => p[1])]);
      // index base line: above 100 = money made
      chart.lineMarker(0).value(100).stroke(css("--line-strong", "#c3ccd9"), 1, "2 3");
      addLine(chart, bh, { name: "Buy & hold", color: css("--ser-hist", "#0d1420"), width: 1.5, digits: 1 });
      addLine(chart, strat, { name: "Janus strategy", color: css("--ser-fcst", "#2563b8"), width: 2, digits: 1 });
    },
  },
  watch: {
    getTradingStrategy(val) {
      if (val && !this.value) this.value = Object.keys(val)[0] || "";
      this.$nextTick(() => this.draw());
    },
    value() {
      this.$nextTick(() => this.draw());
    },
  },
  mounted() {
    if (this.markets.length) this.value = this.markets[0];
    this.fetchTradingStrategy();
    this.draw();
  },
  beforeUnmount() {
    disposeChart(this.chart);
    this.chart = null;
  },
};
</script>
